import { Trans } from '@lingui/react/macro';
import { isLockedBookingSalesItem } from 'noddi-util';
import { useEffect, useMemo } from 'react';
import { useStationaryBookableServices } from '../../../hooks/useStationaryBookableServices';
import { useStationaryAddonServices, useStationaryBookingActions } from '../../../stores/StationaryBookingStore';
import type { StationaryService } from '../../../types/stationaryBooking';
import { ServiceCard } from './ServiceCard';

interface ExtraServiceCardsProps {
  primaryService: StationaryService;
}

const AddonsTitle = () => {
  return (
    <p className='text-lg font-semibold'>
      <Trans>Do you need help with anything else?</Trans>
    </p>
  );
};

export const ExtraServiceCards = ({ primaryService }: ExtraServiceCardsProps) => {
  const addonsSelectedServices = useStationaryAddonServices();
  const { addAddonService, removeAddonService } = useStationaryBookingActions();
  const { addonServicesBySalesItemId, priceListName } = useStationaryBookableServices();

  const addons = useMemo(
    () => addonServicesBySalesItemId?.[String(primaryService.salesItemId)] ?? [],
    [addonServicesBySalesItemId, primaryService.salesItemId]
  );

  useEffect(() => {
    for (const addon of addons) {
      if (!isLockedBookingSalesItem(addon)) {
        continue;
      }
      const isAlreadySelected = addonsSelectedServices.some(
        (selectedService) => selectedService.salesItemId === addon.salesItemId
      );
      if (!isAlreadySelected) {
        addAddonService(addon);
      }
    }
  }, [addAddonService, addons, addonsSelectedServices]);

  if (addons.length === 0) {
    return null;
  }

  function handleServiceClick(service: StationaryService) {
    if (isLockedBookingSalesItem(service)) {
      return;
    }
    const isAlreadySelected = addonsSelectedServices.some(
      (selectedService) => selectedService.salesItemId === service.salesItemId
    );
    if (isAlreadySelected) {
      removeAddonService(service.salesItemId);
    } else {
      addAddonService(service);
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      <AddonsTitle />
      <div className='flex flex-col gap-4'>
        {addons.map((addon) => (
          <ServiceCard
            key={addon.salesItemId}
            service={addon}
            isAlreadySelected={addonsSelectedServices.some(
              (selectedService) => selectedService.salesItemId === addon.salesItemId
            )}
            handleServiceClick={handleServiceClick}
            contextPriceListName={priceListName}
          />
        ))}
      </div>
    </div>
  );
};
